<template>
    <ValidationObserver
        class="login-form"
        ref="observer"
        tag="form"
        @submit.prevent="setNewPassword"
        v-slot="{ invalid }"
    >
        <div class="text-center mb-3">
            <h5 class="mb-0">{{$t('auth.set_new_password')}}</h5>
            <span class="d-block text-muted">
                {{$t('auth.password_needs_to_contain')}}
                <span :class="{'text-success': hasAtLeastEightCharacters}"> {{$t('auth.password_needs_eight_characters')}}</span>,
                <span :class="{'text-success': hasAtLeastOneBigLetter}"> {{$t('auth.password_needs_one_capital_letter')}}</span>
                {{$t('auth.password_needs_and')}}
                <span :class="{'text-success': hasAtLeastOneNumber}">{{$t('auth.password_needs_one_digit')}}</span>.
            </span>
        </div>

        <FormSlot name="password"
                  :label="$t('auth.new_password')"
                  parentClass="form-group form-group-feedback form-group-feedback-left"
                  :showLabel="false"
                  :rules="{
                      passwordValidation: true,
                      required: true
                  }"
                  v-slot="{errors}"
        >
            <input v-model="password"
                   type="password"
                   ref="password"
                   autocomplete="new-password"
                   class="form-control"
                   :placeholder="$t('auth.new_password')"
            >
            <div class="form-control-feedback">
                <i class="icon-lock2 text-muted"></i>
            </div>
        </FormSlot>

        <FormSlot name="passwordRepeat"
                  :label="$t('auth.repeat_password')"
                  parentClass="form-group form-group-feedback form-group-feedback-left"
                  :showLabel="false"
                  :rules="{
                      passwordRepeat: '@password',
                      required: true
                  }"
                  v-slot="{errors}"
        >
            <input v-model="passwordRepeat"
                   type="password"
                   autocomplete="new-password"
                   class="form-control"
                   :placeholder="$t('auth.repeat_password')"
            >
            <div class="form-control-feedback">
                <i class="icon-lock2 text-muted"></i>
            </div>
        </FormSlot>

        <FadeTransition>
            <div v-if="savingPasswordError" class="alert bg-danger text-white">
                <span class="font-weight-semibold">
                    {{$t('auth.set_new_password_error')}}
                </span>
            </div>
        </FadeTransition>

        <div v-if="!passwordHasBeenChanged" class="form-group">
            <button v-if="!processing"
                    type="submit"
                    :disabled="invalid"
                    class="btn btn-primary btn-block"
            >
              {{$t('auth.set_new_password_button')}} <fa-icon icon="key" class="ml-2" />
            </button>

            <button v-else-if="passwordHasBeenChanged"
                    disabled="disabled"
                    type="button"
                    class="btn btn-primary btn-block"
            >
              {{$t('auth.redirecting_to_login_page')}} <fa-icon icon="spinner" spin class="ml-2" />
            </button>

            <button v-else
                    disabled="disabled"
                    type="button"
                    class="btn btn-primary btn-block"
            >
              {{$t('auth.setting_new_password')}} <fa-icon icon="spinner" spin class="ml-2" />
            </button>
        </div>

        <div class="text-center">
            <router-link :to="{name: 'Login'}" class="btn btn-link">
              {{$t('auth.go_back_to_log_in_page')}}
            </router-link>
        </div>
    </ValidationObserver>
</template>

<script>

    import {ValidationObserver, extend} from "vee-validate";
    import {FadeTransition} from 'vue2-transitions';
    import {FormSlot} from '@dealer-group/dealerui';
    import {passwordValidation, passwordRepeat} from "../rules/password";
    import {SET_PASSWORD} from "../store/types";
    import {FLASH_DELAY} from "../constants";
    import store from "../store/store";

    extend('passwordValidation', passwordValidation);
    extend('passwordRepeat', passwordRepeat);

    export default {
        name: "SetNewPassword",
        components: {FormSlot, FadeTransition, ValidationObserver},
        data: () => ({
            password: '',
            passwordRepeat: '',
            processing: false,
            savingPasswordError: false,
            passwordHasBeenChanged: false
        }),

        mounted() {
            this.focusPassword();
        },

        computed: {
            ...store.mapState(['user', 'email']),

            hasAtLeastEightCharacters() { return this.password.length >= 8; },
            hasAtLeastOneBigLetter() { return !! this.password.match(/[A-Z]+/); },
            hasAtLeastOneNumber() { return !! this.password.match(/[0-9]+/); },
        },

        methods: {
            focusPassword() {
                this.$nextTick(() => this.$refs.password.focus())
            },

            async setNewPassword() {
                const isValid = await this.$refs.observer.validate();

                if (! isValid) {
                    return;
                }

                this.savingPasswordError = false;
                this.processing = true;

                store
                    .dispatch(SET_PASSWORD, this.password)
                    .then(() => {
                        this.passwordHasBeenChanged = true;
                        this.savingPasswordError = false;

                        this.$flash.success(
                            this.$t('auth.flash_password_was_change'),
                            {delay: FLASH_DELAY}
                        );

                        this.$router.push({ name: 'Login', query: { email: this.email } });
                    })
                    .catch(() => {
                        this.savingPasswordError = true;
                        this.$flash.error(this.$t('auth.flash_password_change_error'), {delay: FLASH_DELAY});
                    })
                    .finally(() => this.processing = false);
            }
        }
    }
</script>
